<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AdReferralCard from './AdReferralCard.vue';
import ArquivoDoCelular from './ArquivoDoCelular.vue';
import BotoesDoModelo from './BotoesDoModelo.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

// O aviso de arquivo que ficou no celular do escritório (troca para a API
// oficial, 23–24/09/2026, e a conferência): oferece anexar o arquivo ali.
const AVISO_DE_ARQUIVO =
  '📎 Arquivo recebido enquanto o advup estava sem receber';
const arquivoNoCelular = computed(
  () =>
    contentAttributes.value?.arquivoNoCelular === true ||
    (contentAttributes.value?.arquivoNoCelular !== false &&
      (content.value || '').startsWith(AVISO_DE_ARQUIVO))
);

// O aviso de mensagem que a Meta não repassou (primeiro contato, enquete,
// visualização única…): o texto está só no celular; oferece copiar de lá.
const AVISOS_SEM_CONTEUDO = [
  '⚠️ Mensagem do tipo',
  '⚠️ Chegou algo que o WhatsApp oficial',
  '⚠️ Primeira mensagem de contato novo',
  '⚠️ Uma mensagem que o WhatsApp oficial',
];
const semConteudo = computed(
  () =>
    contentAttributes.value?.semConteudo === true ||
    (contentAttributes.value?.semConteudo !== false &&
      !contentAttributes.value?.copiadoDoCelular &&
      !contentAttributes.value?.anexadoDoCelular &&
      AVISOS_SEM_CONTEUDO.some(a => (content.value || '').startsWith(a)))
);
const copiadoPor = computed(
  () => contentAttributes.value?.copiadoDoCelular?.por || ''
);

// Modelo aprovado com respostas rápidas (24/10/2026): os botões aparecem sob o texto; e a
// resposta do cliente que veio de um toque ganha a marca "respondeu pelo botão".
const botoesDoModelo = computed(
  () => contentAttributes.value?.modeloEnviado?.botoes || []
);
const respostaPorBotao = computed(
  () => contentAttributes.value?.respostaPorBotao || ''
);

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <AdReferralCard
        v-if="contentAttributes.referral"
        :referral="contentAttributes.referral"
      />
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <ArquivoDoCelular
        v-if="arquivoNoCelular || semConteudo"
        :com-texto="semConteudo"
      />
      <span v-if="copiadoPor" class="text-xs text-n-slate-10">
        {{
          $t('CONVERSATION.ARQUIVO_DO_CELULAR.COPIADO_POR', {
            nome: copiadoPor,
          })
        }}
      </span>
      <span v-if="respostaPorBotao" class="resposta-por-botao">
        <i class="i-ph-hand-tap" aria-hidden="true" />
        {{ $t('CONVERSATION.BOTOES_DO_MODELO.RESPONDEU') }}
      </span>
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <BotoesDoModelo v-if="botoesDoModelo.length" :botoes="botoesDoModelo" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>

<style scoped>
.resposta-por-botao {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  margin-top: calc(var(--esp-2) * -1);
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
}
</style>
